// Google Analytics 4 (gtag.js) integration.
//
// Configured via the `VITE_GA_ID` env var. If it's unset, this module is a
// no-op — no gtag script loads, no events fire, no network calls happen.
//
// To enable:
//   - Locally: add `VITE_GA_ID=G-XXXXXXXXXX` to `.env.local`
//   - On CI:   add a `VITE_GA_ID` GitHub secret, forwarded by the workflow

const GA_ID = import.meta.env.VITE_GA_ID;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

let initialized = false;

export function initAnalytics() {
  if (!GA_ID || initialized) return;
  initialized = true;

  // Inject the gtag.js loader (async, doesn't block render).
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer ?? [];
  // Function-keyword form so `arguments` is available — this mirrors
  // Google's official inline snippet, which pushes the Arguments object
  // onto dataLayer verbatim.
  function gtag(this: unknown) {
    (window.dataLayer as unknown[]).push(arguments);
  }
  window.gtag = gtag as (...args: unknown[]) => void;

  window.gtag("js", new Date());
  // We fire our own page_view event from React on every route change, so
  // turn off gtag's auto-pageview to avoid double-counting the initial load.
  window.gtag("config", GA_ID, { send_page_view: false });
}

export function trackPageView(path: string) {
  if (!GA_ID || !window.gtag) return;
  window.gtag("event", "page_view", {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
}
